import { createContext, ReactNode, useContext, useId } from 'react';
import cx from 'classnames';

export interface FieldProps {
  label?: ReactNode;
  /** The size of the control inside: `sm` takes a smaller label and message. */
  size?: ControlSize;
  required?: boolean;
  /** A small «- обязательное поле» beside the label, for forms that mark required fields in words. */
  requiredHint?: boolean;
  /** Shown under the control while there is no error. */
  hint?: ReactNode;
  error?: string;
  /** Marks the control as wrong without a message — when the message is shown elsewhere. */
  invalid?: boolean;
  className?: string;
  children: ReactNode;
}

interface FieldContextValue {
  id: string;
  invalid: boolean;
  describedBy?: string;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** What a control inside a `Field` needs to be tied to its label and message. */
export const useField = () => useContext(FieldContext);

/** The look of a field without its size: border, fill, focus, disabled and invalid states. */
export const controlSurfaceClasses = (invalid: boolean) =>
  cx(
    'rounded-control border bg-surface text-ink placeholder:text-ink-subtle',
    // The focused border reads as 2px; a ring draws the second pixel outside, so the text does not shift.
    'focus:outline-none focus:ring-1',
    'disabled:cursor-not-allowed disabled:border-line-strong disabled:bg-surface-muted disabled:text-ink-subtle',
    invalid ? 'border-danger focus:ring-danger' : 'border-line-strong focus:border-ink focus:ring-ink',
  );

/** The classes every text control shares; `Input` and `Textarea` add their own box size. */
export const controlClasses = (invalid: boolean) => cx('w-full text-body-l', controlSurfaceClasses(invalid));

export type ControlSize = 'sm' | 'md' | 'lg';

/** Height and side padding of a one-line control. */
export const controlSizeClasses: Record<ControlSize, string> = {
  // 16px on phones: iOS Safari zooms the page into any field with smaller text.
  sm: 'h-8 px-3 sm:text-ui',
  md: 'h-10 px-4',
  lg: 'h-12 px-5',
};

/** Label, hint and error around one form control — the control itself goes in `children`. */
export const Field = ({
  label,
  size,
  required,
  requiredHint,
  hint,
  error,
  invalid,
  className,
  children,
}: FieldProps) => {
  const isSmall = size === 'sm';
  const id = useId();
  const messageId = `${id}-message`;
  const message = error || hint;

  return (
    <div className={cx('flex w-full min-w-0 flex-col gap-2', className)}>
      {label && (
        <div className="flex items-baseline gap-2">
          <label htmlFor={id} className={cx('text-ink', isSmall ? 'text-ui' : 'text-body-l')}>
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
          {requiredHint && <span className="text-ui-xs text-ink-subtle">- обязательное поле</span>}
        </div>
      )}
      <FieldContext.Provider
        value={{ id, invalid: invalid || Boolean(error), describedBy: message ? messageId : undefined }}
      >
        {children}
      </FieldContext.Provider>
      {message && (
        <p
          id={messageId}
          role={error ? 'alert' : undefined}
          className={cx(isSmall ? 'text-ui-xs' : 'text-ui', error ? 'text-danger' : 'text-ink-subtle')}
        >
          {message}
        </p>
      )}
    </div>
  );
};
